import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('portal.nrich.canvas.kg');
import { OnInit, OnDestroy, Input, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

declare const wiz: any;

const CLASS_COLORS: any = {
    Person: { bg: '#e8d5f5', border: '#9b59b6', font: '#6c3483' },
    Place: { bg: '#d5f5e3', border: '#27ae60', font: '#1e8449' },
    Temple: { bg: '#fdebd0', border: '#e67e22', font: '#a04000' },
    Artwork: { bg: '#d6eaf8', border: '#3498db', font: '#21618c' },
    Period: { bg: '#f9e79f', border: '#f1c40f', font: '#7d6608' },
    Technique: { bg: '#fadbd8', border: '#e74c3c', font: '#922b21' },
    Material: { bg: '#d5dbdb', border: '#7f8c8d', font: '#515a5a' },
    Event: { bg: '#d4efdf', border: '#1abc9c', font: '#0e6655' },
    Organization: { bg: '#e8daef', border: '#8e44ad', font: '#6c3483' },
    Concept: { bg: '#ebf5fb', border: '#5dade2', font: '#2471a3' },
    Thing: { bg: '#f2f3f4', border: '#aab7b8', font: '#616a6b' },
};

@Component({
    selector: 'wiz-portal-nrich-canvas-kg',
template: templateSource || '',
    styles: [``],
})
export class PortalNrichCanvasKgComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
    @Input() documentId: string = '';
    @Input() pdfName: string = '';
    @Input() currentPage: number = 1;

    @ViewChild('kgContainer') containerRef!: ElementRef<HTMLDivElement>;

    public loading: boolean = true;
    public error: string = '';
    public stats: string = '';
    public imageOnly: boolean = false;
    public empty: boolean = false;

    // 엔터티 팝업
    public showPopup: boolean = false;
    public popupEntity: any = null;
    public popupRelations: any[] = [];

    private network: any = null;
    private initialized: boolean = false;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
    }

    public ngAfterViewInit() {
        this.initialized = true;
        if (this.documentId) {
            this.loadGraph();
        }
    }

    public async ngOnChanges(changes: SimpleChanges) {
        if (!this.initialized) return;
        if ((changes['currentPage'] || changes['documentId']) && this.documentId) {
            await this.loadGraph();
        }
    }

    public ngOnDestroy() {
        this.destroyNetwork();
    }

    public async toggleImageOnly() {
        this.imageOnly = !this.imageOnly;
        await this.loadGraph();
    }

    public async loadGraph() {
        this.loading = true;
        this.error = '';
        this.empty = false;
        await this.service.render();

        try {
            const params: any = {
                document_id: this.documentId,
                page: this.currentPage,
            };
            if (this.imageOnly) {
                params.entity_type = 'image';
            }

            const { code, data } = await wiz.call('kg_graph', params);
            if (code !== 200) {
                this.error = 'KG 데이터 로드 실패';
                this.loading = false;
                await this.service.render();
                return;
            }

            if (!data.nodes || data.nodes.length === 0) {
                this.empty = true;
                this.stats = '';
                this.destroyNetwork();
                this.loading = false;
                await this.service.render();
                return;
            }

            this.stats = `노드 ${data.nodes.length} · 엣지 ${data.edges.length}`;
            this.loading = false;
            await this.service.render();

            // vis-network 렌더링 (DOM 준비 대기)
            setTimeout(() => this.renderGraph(data), 50);
        } catch (e: any) {
            this.error = 'KG 데이터 로드 실패';
            this.loading = false;
            await this.service.render();
        }
    }

    private async renderGraph(data: any) {
        this.destroyNetwork();

        const container = this.containerRef?.nativeElement;
        if (!container) return;

        const { DataSet } = await import('vis-data/standalone');
        const { Network } = await import('vis-network/standalone');

        const nodes = data.nodes.map((n: any) => {
            const colors = CLASS_COLORS[n.entity_class] || CLASS_COLORS.Thing;
            const icon = n.entity_type === 'image' ? '🖼️ ' : '';
            return {
                id: n.id,
                label: icon + n.label,
                color: { background: colors.bg, border: colors.border, highlight: { background: colors.bg, border: '#1a1d26' } },
                font: { color: colors.font, size: 13, face: 'Noto Sans KR, sans-serif' },
                shape: 'box',
                borderWidth: 1.5,
                borderWidthSelected: 2.5,
                margin: { top: 8, bottom: 8, left: 10, right: 10 },
                _data: n,
            };
        });

        const edges = data.edges.map((e: any) => ({
            id: e.id,
            from: e.source,
            to: e.target,
            label: e.predicate_label || e.predicate,
            arrows: 'to',
            color: { color: '#aab7b8', highlight: '#3366cc' },
            font: { size: 10, color: '#7f8c8d', strokeWidth: 2, strokeColor: '#fff' },
            smooth: { type: 'curvedCW', roundness: 0.15 },
        }));

        const networkData = {
            nodes: new DataSet(nodes),
            edges: new DataSet(edges),
        };

        const options = {
            physics: {
                solver: 'forceAtlas2Based',
                forceAtlas2Based: {
                    gravitationalConstant: -40,
                    centralGravity: 0.005,
                    springLength: 120,
                    springConstant: 0.06,
                    damping: 0.5,
                },
                stabilization: { iterations: 150, fit: true },
            },
            interaction: { hover: true, tooltipDelay: 200, zoomView: true, dragView: true },
            layout: { improvedLayout: true },
        };

        this.network = new Network(container, networkData, options);

        this.network.on('click', (params: any) => {
            if (params.nodes.length > 0) {
                this.showEntityPopup(params.nodes[0]);
            }
        });
    }

    private destroyNetwork() {
        if (this.network) {
            this.network.destroy();
            this.network = null;
        }
    }

    public async showEntityPopup(entityId: string) {
        try {
            const { code, data } = await wiz.call('kg_entity_get', { entity_id: entityId });
            if (code === 200) {
                this.popupEntity = data.entity;
                this.popupRelations = data.relations || [];
                this.showPopup = true;
                await this.service.render();
            }
        } catch (e) {
            console.error('Entity popup error:', e);
        }
    }

    public closePopup() {
        this.showPopup = false;
        this.popupEntity = null;
        this.popupRelations = [];
        this.service.render();
    }

    public getTypeBadge(entity: any): string {
        if (entity.entity_type === 'image') {
            return `🖼️ 이미지${entity.image_id ? ` (${entity.image_id})` : ''}`;
        }
        return '📝 텍스트';
    }

    public getPageStr(entity: any): string {
        const pages = entity.pages || [];
        return pages.map((p: any) => `${p.page}p${p.keyword ? ` (${p.keyword})` : ''}`).join(', ') || '(매핑된 페이지 없음)';
    }
}

export default PortalNrichCanvasKgComponent;